/**
 * SellerBillingContext — single source of truth for the seller's
 * SaaS subscription state. Reads `/api/billing/status` once and exposes
 * it via context so the dashboard banner, the sidebar lock, the
 * onboarding gate, and the Plan & Billing panel all share the same
 * fetch.
 *
 * Refreshes are explicit: call `refresh()` after subscribe / change-plan
 * / cancel / wallet topup so the UI reflects the new state without a
 * page reload.
 *
 * Only seller-role users actually hit `/billing/status` — for buyers
 * and admins this hook returns a permissive "active" placeholder so
 * gates and banners no-op.
 */
import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import api from "../lib/api";
import { useAuth } from "./AuthContext";


const SellerBillingCtx = createContext({
  status: null,
  loading: true,
  refresh: () => {},
});

const PASSTHROUGH = { status: "active", plan_id: null, _placeholder: true };


export function SellerBillingProvider({ children }) {
  const { user } = useAuth();
  const isSeller = !!(user && user.role === "seller");
  const [status, setStatus]   = useState(() => (isSeller ? null : PASSTHROUGH));
  const [loading, setLoading] = useState(isSeller);
  const [tick, setTick]       = useState(0);

  // Stable refresh trigger — does not change between renders, so consumers
  // can include it in their dep arrays without triggering loops.
  const refresh = useCallback(() => setTick(n => n + 1), []);

  useEffect(() => {
    if (!isSeller) {
      // Defer the synchronous setState to a microtask so the eslint
      // react-hooks/set-state-in-effect heuristic doesn't trip on the
      // sync branch (lazy-init already covers the very first render).
      Promise.resolve().then(() => {
        setStatus(PASSTHROUGH);
        setLoading(false);
      });
      return;
    }
    let cancelled = false;
    setLoading(true);
    api.get("/billing/status")
      .then(r => { if (!cancelled) setStatus(r.data); })
      .catch(() => { if (!cancelled) setStatus(PASSTHROUGH); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [isSeller, tick]);

  const value = useMemo(() => ({ status, loading, refresh }), [status, loading, refresh]);
  return (
    <SellerBillingCtx.Provider value={value}>
      {children}
    </SellerBillingCtx.Provider>
  );
}


export const useSellerBilling = () => useContext(SellerBillingCtx);
